import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { FaQuoteLeft, FaArrowRight } from 'react-icons/fa';
import SamanthaPhoto from '../assets/samantha-photo.jpg';
import { Link } from 'react-router-dom';

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <AboutContainer id="about">
      <div className="container">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          My Personal Journey
        </SectionTitle>
        
        <AboutContent ref={ref}>
          <AboutImageContainer
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ImageFrame>
              <img src={SamanthaPhoto} alt="Samantha Knowles" />
              <ShapeAccent className="accent-1" />
              <ShapeAccent className="accent-2" />
            </ImageFrame>
          </AboutImageContainer>
          
          <AboutTextContainer
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {/* <SubHeading>Certified Development Coach</SubHeading> */}
            <p>I was brought up and educated in a working-class family in the Northwest of England. I worked hard. I enjoyed time alone with my thoughts. I didn't step in to uncomfortable moments to share my opinions or to open up conversations which might require my input. I was a timid, a more observing middle child who lived in my head and felt the outside world was looking at me through judging eyes. I pulled the shutters down and sealed up. My myopic lens was my comfortable space.</p>
            
            <LearnMoreButton href="/my-journey">
              Learn More About My Journey <FaArrowRight />
            </LearnMoreButton>
            
            <CredentialsList>
              {["Certified Development Coach", "Senior Director", "Legal Professional"].map((credential, index) => (
                <CredentialBadge 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.3, delay: 0.6 + (index * 0.1) }}
                >
                  {credential}
                </CredentialBadge>
              ))}
            </CredentialsList>
            
            <AboutPhilosophyContainer>
              <QuoteIconWrapper>
                <FaQuoteLeft />
              </QuoteIconWrapper>
              <AboutPhilosophy>
                Life reshapes us in silence – every challenge, a chisel; every change, a mirror. Transformation doesn't ask for permission; it simply arrives, unfolding who we were meant to become
                <QuoteAttribution>- Rimi</QuoteAttribution>
              </AboutPhilosophy>
            </AboutPhilosophyContainer>
          </AboutTextContainer>
        </AboutContent>
      </div>
      <BackgroundElement className="element-1" />
      <BackgroundElement className="element-2" />
      <DiagonalDivider />
    </AboutContainer>
  );
};

const AboutContainer = styled.section`
  position: relative;
  overflow: hidden;
  padding: 100px 0 120px;
  background-color: white;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 70px;
  position: relative;
  color: #1A2A3A;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #A8C4A0;
  }
`;

const AboutContent = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const AboutImageContainer = styled(motion.div)`
  flex: 0.35;
  position: relative;
  
  @media (max-width: 992px) {
    width: 80%;
    max-width: 400px;
    margin: 0 auto 40px;
  }
`;

const ImageFrame = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 125%; /* Controls the aspect ratio */
  
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.18);
    filter: contrast(1.05);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 2px solid #A8C4A0;
    border-radius: 10px;
    z-index: -1;
  }
`;

const ShapeAccent = styled.div`
  position: absolute;
  border-radius: 50%;
  
  &.accent-1 {
    width: 180px;
    height: 180px;
    background-color: rgba(168, 196, 160, 0.15);
    top: -50px;
    right: -50px;
    z-index: -1;
  }
  
  &.accent-2 {
    width: 130px;
    height: 130px;
    background-color: rgba(90, 127, 140, 0.15);
    bottom: -30px;
    right: 30px;
    z-index: -1;
  }
`;

const AboutTextContainer = styled(motion.div)`
  flex: 0.65;
  
  p {
    margin-bottom: 20px;
    font-size: 1.05rem;
    line-height: 1.7;
    color: #555;
  }
  
  @media (max-width: 992px) {
    text-align: center;
  }
`;


const LearnMoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  color: #5A7F8C;
  font-weight: 600;
  text-decoration: none;
  margin-top: 10px;
  margin-bottom: 30px;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 8px;
    transition: transform 0.3s ease;
    font-size: 0.9rem;
  }
  
  &:hover {
    color: #A8C4A0;
    
    svg {
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const CredentialsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 15px 0 35px;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const CredentialBadge = styled(motion.span)`
  background: linear-gradient(135deg, rgba(168, 196, 160, 0.2) 0%, rgba(90, 127, 140, 0.2) 100%);
  color: #1A2A3A;
  padding: 10px 20px;
  border-radius: 30px;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
  }
`;

const AboutPhilosophyContainer = styled.div`
  position: relative;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.95) 100%);
  backdrop-filter: blur(5px);
  padding: 30px 30px 30px 50px;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  border-left: 4px solid #A8C4A0;
  margin-top: 40px;
`;

const QuoteIconWrapper = styled.div`
  position: absolute;
  top: -16px;
  left: 20px;
  background-color: white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: #A8C4A0;
  font-size: 16px;
`;

const AboutPhilosophy = styled.blockquote`
  font-style: italic;
  margin: 0;
  color: #1A2A3A;
  font-size: 1.1rem;
  line-height: 1.6;
  font-weight: 300;
`;

const QuoteAttribution = styled.div`
  display: block;
  text-align: right;
  margin-top: 15px;
  font-weight: 500;
  font-size: 0.9rem;
  font-style: normal;
  color: #5A7F8C;
`;

const BackgroundElement = styled.div`
  position: absolute;
  
  &.element-1 {
    width: 600px;
    height: 600px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    top: -200px;
    right: -200px;
    z-index: 0;
    transform: rotate(-15deg);
  }
  
  &.element-2 {
    width: 500px;
    height: 500px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h50v50H0z' fill='%235A7F8C' fill-opacity='0.03'/%3E%3C/svg%3E");
    bottom: -100px;
    left: -150px;
    z-index: 0;
    transform: rotate(10deg);
  }
`;

const DiagonalDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-color: #f9f9f9;
  clip-path: polygon(0 100%, 100% 40%, 100% 100%, 0 100%);
  z-index: 1;
`;

export default About;